import { RequestService } from './request.service';
import { Injectable, signal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import * as Sentry from '@sentry/angular';

/**
 * Event types emitted by the sync service
 */
export interface SyncEvent {
	state: boolean;
	type: 'connected' | 'data' | 'error' | 'disconnected';
	data: {
		id: string;
		event: any;
	};
	errorMessage: string;
}

@Injectable({ providedIn: 'root' })
export class SyncService {
	/**
	 * Signal indicating if the WebSocket is currently connected.
	 * Components can use this to know when it's safe to make requests
	 * that expect WebSocket responses.
	 */
	public readonly isConnected = signal<boolean>(false);

	/**
	 * Promise that resolves when WebSocket is connected.
	 * Use this to await connection before triggering requests.
	 */
	private connectionReadyResolve: (() => void) | null = null;
	private connectionReadyPromise: Promise<void> | null = null;

	constructor(private requestService: RequestService) {}

	/**
	 * Returns a promise that resolves when the WebSocket connection is established.
	 * Call this before making GraphQL requests that expect WebSocket responses.
	 */
	public waitForConnection(): Promise<void> {
		if (this.isConnected()) {
			return Promise.resolve();
		}
		if (!this.connectionReadyPromise) {
			this.connectionReadyPromise = new Promise(resolve => {
				this.connectionReadyResolve = resolve;
			});
		}
		return this.connectionReadyPromise;
	}

	getSyncEvents(channel: string): Observable<SyncEvent> {
		// Use ReplaySubject to buffer the 'connected' event for late subscribers
		const subject = new ReplaySubject<SyncEvent>(1);
		let connection: any = null;
		let wsTransaction: any = null;
		let connectionSpan: any = null;

		// Start a transaction for the WebSocket connection lifecycle
		wsTransaction = Sentry.startInactiveSpan({
			name: `websocket.connection`,
			op: 'websocket.lifecycle',
			attributes: {
				channel,
				'websocket.channel': channel,
			},
		});

		// Start connection asynchronously
		this.requestService
			.getEventsChannel(channel)
			.then(conn => {
				connection = conn;

				// Mark as connected BEFORE subscribing to data
				this.isConnected.set(true);
				if (this.connectionReadyResolve) {
					this.connectionReadyResolve();
					this.connectionReadyResolve = null;
				}

				// Create span for connection established
				const connectSpan = Sentry.startInactiveSpan({
					name: 'websocket.connected',
					op: 'websocket.event',
					attributes: {
						channel,
						'websocket.state': 'connected',
					},
				});
				connectSpan?.end();

				subject.next({
					state: true,
					type: 'connected',
					data: { id: '', event: { channel } },
					errorMessage: '',
				});

				// Now subscribe to data events
				connection.subscribe({
					next: (data: any) => {
						const wsEvent = data?.event;
						// Avoid full-payload JSON.stringify on every WS message (JEP-10271 hot path).
						const payloadSize =
							typeof data === 'string'
								? data.length
								: JSON.stringify(data).length;
						const messageAttributes: Record<
							string,
							string | number
						> = {
							channel,
							'websocket.message.id': data?.id,
							'websocket.message.type': wsEvent?.type,
							'websocket.message.size': payloadSize,
						};
						if (wsEvent?.requestId) {
							messageAttributes['graphql.request_id'] =
								wsEvent.requestId;
						}
						if (wsEvent?.cardId) {
							messageAttributes['graphql.card_id'] =
								wsEvent.cardId;
						}

						// Create span for each message
						const messageSpan = Sentry.startInactiveSpan({
							name: 'websocket.message',
							op: 'websocket.event',
							attributes: messageAttributes,
						});

						messageSpan?.end();

						subject.next({
							state: true,
							type: 'data',
							data: {
								id: data?.id,
								event: data?.event,
							},
							errorMessage: '',
						});
					},
					error: (err: any) => {
						// Create span for error
						const errorSpan = Sentry.startInactiveSpan({
							name: 'websocket.error',
							op: 'websocket.event',
							attributes: {
								channel,
								'websocket.error': String(err),
							},
						});
						errorSpan?.end();

						// End the connection transaction with error status
						wsTransaction?.setStatus({ code: 2, message: 'error' });
						wsTransaction?.end();

						this.isConnected.set(false);
						subject.next({
							state: false,
							type: 'error',
							data: {
								id: '',
								event: {
									completed: 0,
									total: 0,
									syncStartDate: new Date(),
									entity: 0,
									channel: '',
								},
							},
							errorMessage: err,
						});
						subject.complete();
					},
				});
			})
			.catch(error => {
				// Create span for connection failure
				const failSpan = Sentry.startInactiveSpan({
					name: 'websocket.connection.failed',
					op: 'websocket.event',
					attributes: {
						channel,
						'websocket.error': String(error),
					},
				});
				failSpan?.end();

				// End transaction with error
				wsTransaction?.setStatus({
					code: 2,
					message: 'connection_failed',
				});
				wsTransaction?.end();

				this.isConnected.set(false);
				subject.error(error);
				subject.complete();
			});

		// Return observable that shares the connection
		return subject.asObservable();
	}

	/**
	 * Resets the connection state. Call when navigating away or on cleanup.
	 */
	public resetConnectionState(): void {
		// Create span for disconnection
		const disconnectSpan = Sentry.startInactiveSpan({
			name: 'websocket.disconnected',
			op: 'websocket.event',
			attributes: {
				'websocket.state': 'disconnected',
			},
		});
		disconnectSpan?.end();

		this.isConnected.set(false);
		this.connectionReadyPromise = null;
		this.connectionReadyResolve = null;
	}
}
